import React from "react";
import {
  useController,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import {
  Radio,
  RadioProps,
  FormControlLabel,
  FormControlLabelProps,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";

const Error = styled.div`
  color: ${({ theme }) => theme.palette.error.main};
  margin-left: ${({ theme }) => theme.spacing(6.75)};
  margin-top: ${({ theme }) => theme.spacing(-0.75)};
`;

interface Props {
  name: string;
  label?: FormControlLabelProps["label"];
  value: string;
}

type FormInputProps<T extends FieldValues> = RadioProps &
  Props &
  UseControllerProps<T>;

export const RHFRadio = <T extends FieldValues>(
  props: FormInputProps<T>
): React.ReactNode => {
  const { control, name, value, defaultValue, label, style, ...rest } = props;

  const {
    field,
    fieldState: { error },
  } = useController<T>({ control, name, defaultValue });

  return (
    <div>
      <FormControlLabel
        label={label}
        control={
          <Radio
            {...field}
            checked={field.value === value}
            value={value}
            name={name}
            onChange={(event, value) => {
              field.onChange(event.target.value);
            }}
            {...rest}
          />
        }
      />
      {error?.message && (
        <Error>
          <Typography
            variant="caption"
            color="inherit"
            data-testid="rhf-radio-error"
          >
            {error.message}
          </Typography>
        </Error>
      )}
    </div>
  );
};
